import { lazy } from 'react';

// Recuperación de chunks que ya no existen ------------------------------------
//
// Cada build de Vite hashea los assets (`/assets/RolesListPage-CG7TbXXz.js`).
// Cuando Cloud Run sirve una revisión nueva, los hashes viejos dejan de existir.
// Una pestaña abierta con el build anterior sigue viva: navegar a una ruta cuyo
// chunk todavía no había cargado dispara un `import()` que rechaza y deja al
// usuario frente a la pantalla de error por defecto.
//
// La salida es recargar la pestaña una vez: el index.html nuevo trae los hashes
// nuevos. Hay que hacerlo con memoria, porque si el fallo no viene del deploy
// (red caída, proxy corporativo) recargar en loop es peor que mostrar el error.

/** Clave de la marca de recarga. `sessionStorage`: vive solo en esta pestaña. */
export const CHUNK_RELOAD_STORAGE_KEY = 'commerce-engine:chunk-reload-at';

/** Ventana dentro de la cual no se vuelve a recargar por el mismo motivo. */
export const CHUNK_RELOAD_WINDOW_MS = 10_000;

// Mensajes de cada motor ante un módulo dinámico que no se pudo traer.
// Chromium y Firefox nombran la URL; Safari solo dice que falló.
const CHUNK_LOAD_ERROR_MESSAGES = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
];

// Lectura defensiva: el rechazo de un `import()` no siempre es un `Error` del
// mismo realm (iframes, extensiones), así que no alcanza con `instanceof`.
function readErrorField(error: unknown, field: 'message' | 'name'): string {
  if (typeof error === 'string') return field === 'message' ? error : '';
  if (typeof error !== 'object' || error === null) return '';
  const value = (error as Record<string, unknown>)[field];
  return typeof value === 'string' ? value : '';
}

/**
 * True cuando el error corresponde a un chunk que el servidor ya no tiene.
 * Pura: solo mira el error, no toca window ni storage.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = readErrorField(error, 'message').toLowerCase();
  if (CHUNK_LOAD_ERROR_MESSAGES.some((known) => message.includes(known))) {
    return true;
  }
  // Fallback por tipo: un `TypeError` sobre una URL de `/assets/` es un fetch
  // de bundle fallido, aunque el motor lo redacte distinto ("Load failed").
  return readErrorField(error, 'name') === 'TypeError' && message.includes('/assets/');
}

/**
 * Registra el intento de recarga y responde si corresponde recargar.
 * Devuelve false si ya hubo una recarga por chunks dentro de la ventana o si
 * `sessionStorage` no está disponible — sin memoria no hay cómo frenar el bucle.
 */
export function shouldReloadAfterChunkError(now: number = Date.now()): boolean {
  try {
    const raw = window.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY);
    const markedAt = raw === null ? null : Number(raw);
    if (markedAt !== null && Number.isFinite(markedAt) && now - markedAt < CHUNK_RELOAD_WINDOW_MS) {
      return false;
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
    return true;
  } catch {
    // Modo privado o storage bloqueado por política: mejor mostrar el error.
    return false;
  }
}

/**
 * Limpia la marca cuando ya venció. Dentro de la ventana no se borra a
 * propósito: es justo lo que frena el bucle si el chunk vuelve a fallar
 * después de recargar.
 */
export function clearChunkReloadMark(now: number = Date.now()): void {
  try {
    const raw = window.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY);
    if (raw === null) return;
    const markedAt = Number(raw);
    if (Number.isFinite(markedAt) && now - markedAt < CHUNK_RELOAD_WINDOW_MS) return;
    window.sessionStorage.removeItem(CHUNK_RELOAD_STORAGE_KEY);
  } catch {
    // Sin storage no hay marca que limpiar.
  }
}

/**
 * Corre el `import()` de un chunk recargando la pestaña una vez si el asset
 * ya no existe. Cualquier otro error se relanza tal cual.
 */
export async function importWithChunkReload<T>(factory: () => Promise<T>): Promise<T> {
  try {
    const loaded = await factory();
    clearChunkReloadMark();
    return loaded;
  } catch (error) {
    if (!isChunkLoadError(error)) throw error;
    if (!shouldReloadAfterChunkError()) throw error;
    window.location.reload();
    // Promesa que nunca resuelve: el navegador ya está reemplazando el
    // documento y Suspense tiene que seguir mostrando el fallback. Resolver o
    // rechazar acá haría parpadear una pantalla del build viejo.
    return new Promise<T>(() => {});
  }
}

/**
 * Igual que `React.lazy` — misma firma, vía `typeof lazy` — con recuperación
 * del chunk que el deploy se llevó.
 */
export const lazyWithReload: typeof lazy = (factory) => lazy(() => importWithChunkReload(factory));
